import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { useSettingsDialog } from './contexts/SettingsDialogContext';
import { Box, CircularProgress } from '@mui/material';
import Layout from './components/Layout';
import Home from './pages/Home';
import Businesses from './pages/Businesses';
import Devices from './pages/Devices';
import DeviceEvents from './pages/DeviceEvents';
import Admin from './pages/Admin';
import Help from './pages/Help';
import About from './pages/About';
import Login from './components/Login';
import Register from './pages/Register';
import ConfirmEmail from './pages/ConfirmEmail';
import Forbidden from './pages/Forbidden';
import DeviceSettings from './pages/DeviceSettings';
import RequireActiveSubscription from './components/RequireActiveSubscription';
import UserBusinesses from './components/user/UserBusinesses';
import UserBusinessDetails from './components/user/UserBusinessDetails';
import MyContacts from './pages/MyContacts';
import DeviceMap from './components/devices/DeviceMap';
import ImageSortingPage from './pages/cameraUpload/ImageSortingPage';

import AnnotatorProjectListPage from './pages/annotator/AnnotatorProjectListPage';
import AnnotatorProjectPage from './pages/annotator/AnnotatorProjectPage';
import AnnotatorAnnotationListPage from './pages/annotator/AnnotatorAnnotationListPage';
import AnnotatorEditorPage from './pages/annotator/AnnotatorEditorPage';
import AnnotatorExportPage from './pages/annotator/AnnotatorExportPage';
import AnnotatorTrainingPage from './pages/annotator/AnnotatorTrainingPage';
import AnnotatorImportPage from './pages/annotator/AnnotatorImportPage';
import AnnotatorSettingsPage from './pages/annotator/AnnotatorSettingsPage';
import AnnotatorDetectionTestPage from './pages/annotator/AnnotatorDetectionTestPage';
import AnnotatorCombineProjectsPage from './pages/annotator/AnnotatorCombineProjectsPage';
import InferenceModels from './pages/InferenceModels';
import InferenceNodes from './pages/InferenceNodes';
import GetStarted from './pages/GetStarted';
import Reports from './pages/Reports';
import FieldView from './pages/FieldView';

const RequireRole: React.FC<{ role: string; children: React.ReactElement }> = ({ role, children }) => {
  const auth = useAuth();
  if (!auth.user) {
    return <Navigate to="/" replace />;
  }
  return auth.hasRole(role) ? children : <Forbidden />;
};

const RequireAnyRole: React.FC<{ roles: string[]; children: React.ReactElement }> = ({ roles, children }) => {
  const auth = useAuth();
  if (!auth.user) {
    return <Navigate to="/" replace />;
  }
  return roles.some(role => auth.hasRole(role)) ? children : <Forbidden />;
};

function App() {
  const auth = useAuth();
  const { openSettings } = useSettingsDialog();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Returning from Stripe Checkout/Billing Portal — pop Settings open on the
  // Billing tab so the (webhook-driven, so not instant) subscription change
  // is visible, then strip the query param so a refresh doesn't re-trigger it.
  useEffect(() => {
    const checkout = searchParams.get('checkout');
    if (checkout === 'success' || checkout === 'cancelled') {
      openSettings('billing');
      navigate('/', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // AuthProvider restores a stored session from localStorage in an effect,
  // not synchronously on first render — so auth.user is briefly null even
  // for an already-signed-in user on every hard page load. Rendering the
  // logged-out Routes tree during that window (rather than waiting on
  // isLoading) used to unmount+remount anything matching a route present in
  // both trees once auth.user resolved a moment later — e.g. ConfirmEmail,
  // whose effect would then fire its confirm-email request twice, the
  // second attempt failing since the first had already consumed the token.
  if (auth.isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // Show login if not authenticated
  if (!auth.user) {
    return (
      <Routes>
        <Route path="/register" element={<Register />} />
        <Route path="/confirm-email" element={<ConfirmEmail />} />
        <Route path="*" element={<Login />} />
      </Routes>
    );
  }

  return (
    <Layout>
      <Routes>
        {/* Annotator editor - full screen, no padding */}
        <Route
          path="/annotator/projects/:projectId/images/:imageId"
          element={
            <RequireRole role="DMO_ADMIN">
              <AnnotatorEditorPage />
            </RequireRole>
          }
        />

        {/* Dashboard - full-bleed map, no padding */}
        <Route
          path="/"
          element={
            <RequireActiveSubscription>
              <Home />
            </RequireActiveSubscription>
          }
        />

        {/* Everything else gets padding */}
        <Route
          path="*"
          element={
            <Box sx={{ flexGrow: 1, pt: 1, pb: { xs: 1.5, sm: 2 }, px: 2 }}>
              <Routes>
          {/* Normal User Routes */}
          <Route
            path="/my-business"
            element={
              <RequireRole role="DMO_USER">
                <UserBusinesses />
              </RequireRole>
            }
          />
          <Route
            path="/my-business/:id"
            element={
              <RequireRole role="DMO_USER">
                <UserBusinessDetails />
              </RequireRole>
            }
          />
          <Route
            path="/contacts"
            element={
              <RequireRole role="DMO_USER">
                <RequireActiveSubscription>
                  <MyContacts />
                </RequireActiveSubscription>
              </RequireRole>
            }
          />
          <Route
            path="/devices"
            element={
              <RequireRole role="DMO_USER">
                <RequireActiveSubscription>
                  <Devices />
                </RequireActiveSubscription>
              </RequireRole>
            }
          />
          <Route
            path="/image-sorting"
            element={
              <RequireRole role="DMO_USER">
                <RequireActiveSubscription>
                  <ImageSortingPage />
                </RequireActiveSubscription>
              </RequireRole>
            }
          />
          <Route
            path="/devices/map"
            element={
              <RequireRole role="DMO_USER">
                <RequireActiveSubscription>
                  <DeviceMap />
                </RequireActiveSubscription>
              </RequireRole>
            }
          />
          <Route
            path="/devices/:id/events"
            element={
              <RequireRole role="DMO_USER">
                <RequireActiveSubscription>
                  <DeviceEvents />
                </RequireActiveSubscription>
              </RequireRole>
            }
          />
          <Route
            path="/devices/:id/settings"
            element={
              <RequireRole role="DMO_USER">
                <RequireActiveSubscription>
                  <DeviceSettings />
                </RequireActiveSubscription>
              </RequireRole>
            }
          />
          
          {/* Shared Routes for Both Roles */}
          <Route
            path="/get-started"
            element={
              <RequireAnyRole roles={["DMO_USER", "DMO_ADMIN"]}>
                <GetStarted />
              </RequireAnyRole>
            }
          />
          <Route
            path="/businesses"
            element={
              <RequireAnyRole roles={["DMO_USER", "DMO_ADMIN"]}>
                <Businesses />
              </RequireAnyRole>
            }
          />
          <Route
            path="/businesses/:id"
            element={
              <RequireAnyRole roles={["DMO_USER", "DMO_ADMIN"]}>
                <UserBusinessDetails />
              </RequireAnyRole>
            }
          />
          <Route
            path="/reports"
            element={
              <RequireAnyRole roles={["DMO_USER", "DMO_ADMIN"]}>
                <RequireActiveSubscription>
                  <Reports />
                </RequireActiveSubscription>
              </RequireAnyRole>
            }
          />
          
          {/* Admin Only Routes */}
          <Route
            path="/field-view"
            element={
              <RequireRole role="DMO_ADMIN">
                <FieldView />
              </RequireRole>
            }
          />
          <Route
            path="/admin/*"
            element={
              <RequireRole role="DMO_ADMIN">
                <Admin />
              </RequireRole>
            }
          />
          
          {/* Public Routes */}
          <Route path="/help" element={<Help />} />
          <Route path="/about" element={<About />} />
          <Route path="/forbidden" element={<Forbidden />} />
          <Route path="/confirm-email" element={<ConfirmEmail />} />

          {/* Inference Nodes (admin only) */}
          <Route
            path="/inference-nodes"
            element={
              <RequireRole role="DMO_ADMIN">
                <InferenceNodes />
              </RequireRole>
            }
          />

          {/* Inference Models (admin only) */}
          <Route
            path="/inference-models"
            element={
              <RequireRole role="DMO_ADMIN">
                <InferenceModels />
              </RequireRole>
            }
          />

          {/* Annotator Routes (admin only) */}
          <Route
            path="/annotator/projects"
            element={
              <RequireRole role="DMO_ADMIN">
                <AnnotatorProjectListPage />
              </RequireRole>
            }
          />
          <Route
            path="/annotator/combine"
            element={
              <RequireRole role="DMO_ADMIN">
                <AnnotatorCombineProjectsPage />
              </RequireRole>
            }
          />
          <Route
            path="/annotator/projects/:projectId"
            element={
              <RequireRole role="DMO_ADMIN">
                <AnnotatorProjectPage />
              </RequireRole>
            }
          />
          <Route
            path="/annotator/projects/:projectId/images"
            element={
              <RequireRole role="DMO_ADMIN">
                <AnnotatorAnnotationListPage />
              </RequireRole>
            }
          />
          <Route
            path="/annotator/projects/:projectId/export"
            element={
              <RequireRole role="DMO_ADMIN">
                <AnnotatorExportPage />
              </RequireRole>
            }
          />
          <Route
            path="/annotator/projects/:projectId/training"
            element={
              <RequireRole role="DMO_ADMIN">
                <AnnotatorTrainingPage />
              </RequireRole>
            }
          />
          <Route
            path="/annotator/projects/:projectId/detection-test"
            element={
              <RequireRole role="DMO_ADMIN">
                <AnnotatorDetectionTestPage />
              </RequireRole>
            }
          />
          <Route
            path="/annotator/import"
            element={
              <RequireRole role="DMO_ADMIN">
                <AnnotatorImportPage />
              </RequireRole>
            }
          />
          <Route
            path="/annotator/settings"
            element={
              <RequireRole role="DMO_ADMIN">
                <AnnotatorSettingsPage />
              </RequireRole>
            }
          />
              </Routes>
            </Box>
          }
        />
      </Routes>
    </Layout>
  );
}

export default App;
